import BackNextNav from '@/components/common/BackNextNav';
import { FlexRow } from '@/components/common/Flex';
import InviterAvatar from '@/components/common/InviterAvatar';
import { BigHeader, Paragraph } from '@/components/common/Typography';
import UserAvatar from '@/components/common/UserAvatar';
import { useUserData } from '@/services/user';
import { useMutation } from '@apollo/client';
import { Box, FlexProps } from '@chakra-ui/react';
import {
    AcceptPartnershipInviteDocument,
    AllFinancialEntitiesDocument,
    CurrentUserProfileDocument,
    DeclinePartnershipInviteDocument,
} from '@common/graphql/generated';
import { useState } from 'react';
import { Mail02, UserSquare } from '../common/icons';

interface AcceptInviteIntroViewProps extends FlexProps {
    onNext: () => void;
    onBack: () => void;
}

export default function AcceptInviteIntroView({
    onNext,
    onBack,
}: AcceptInviteIntroViewProps) {
    const { incomingPartnershipInvite } = useUserData();
    const [isLoading, setIsLoading] = useState(false);

    const [acceptPartnershipInvite] = useMutation(
        AcceptPartnershipInviteDocument,
        {
            refetchQueries: [
                AllFinancialEntitiesDocument,
                CurrentUserProfileDocument,
            ],
        }
    );

    const [declinePartnershipInvite] = useMutation(
        DeclinePartnershipInviteDocument
    );

    if (!incomingPartnershipInvite) return <></>;

    const accept = async () => {
        if (!incomingPartnershipInvite.id) return;

        setIsLoading(true);
        await acceptPartnershipInvite({
            variables: {
                partnershipInviteId: incomingPartnershipInvite.id,
            },
        });
        setIsLoading(false);
        onNext();
    };

    const decline = async () => {
        if (!incomingPartnershipInvite.id) return;

        setIsLoading(true);
        await declinePartnershipInvite({
            variables: {
                partnershipInviteId: incomingPartnershipInvite.id,
            },
        });
        setIsLoading(false);
        onBack();
    };

    return (
        <>
            <Box bg="gray.100" borderRadius={6} mb={10} p={10}>
                <Box pos="relative" mb={8}>
                    <InviterAvatar zIndex={2} />
                    <UserAvatar left={-1.5} zIndex={1} />
                </Box>

                <BigHeader mb={5}>
                    {incomingPartnershipInvite.inviterName} has invited you to
                    partner up
                </BigHeader>
                <Paragraph color="textMuted" mb={9} maxW={530}>
                    {incomingPartnershipInvite.inviterName} wants to use Plenty
                    with you to reach your financial goals together. Before
                    continuing, make sure you recognize their contact
                    information:
                </Paragraph>
                <FlexRow alignItems="center">
                    <Box flexGrow={1}>
                        <FlexRow alignItems="center" mb={2}>
                            <UserSquare mr={2} />
                            <Paragraph flexGrow={1}>
                                {incomingPartnershipInvite.inviterName}
                            </Paragraph>
                        </FlexRow>
                        <FlexRow alignItems="center" mb={2}>
                            <Mail02 mr={2} />
                            <Paragraph flexGrow={1}>
                                {incomingPartnershipInvite.inviterEmail}
                            </Paragraph>
                        </FlexRow>
                    </Box>
                    <BackNextNav
                        mt={0}
                        isLoading={isLoading}
                        justifyContent="flex-end"
                        nextButtonText="Accept & set permissions"
                        backButtonText="Decline"
                        showBackIcon={false}
                        onNext={accept}
                        onBack={decline}
                        onClickEventParams={{
                            location: 'Accept Invite Intro View',
                        }}
                    />
                </FlexRow>
            </Box>
        </>
    );
}
